/**
 * 첫 방문 장면 (시안 03 README 5.16, 6장 S0).
 *
 * - SceneBackdrop: 전면 장면 사진 + 위·아래 어두운 덮개. 사진 위 글자는 밝은 색(라이트 전용의 예외, 10장 3)
 * - ActMarker: 막 표시 "골목 — 카운터 — 별채", 지금 장면만 밝게
 * - CounterTransition(S0·T1): 「1막 · 카운터에서 차 한잔」 + 주전자 김(MO2). **자동으로 넘기지 않고 탭으로만**(시안 05 결정 21).
 *   문구가 다 나타나기 전(1.2초) 탭은 무시
 * - AnnexTransition(S0·T2): 「2막 · 별채에 다섯 사람이 둘러앉아 있어요」 + 다섯 초상 + 「별채로 건너가기」 + 호롱불(MO3)
 * 모션 최소화 설정이면 페이드·확대 · 김 · 호롱불 없이 바로 보입니다.
 */
import { useEffect, useRef, useState } from 'react';
import { LampFlicker, Steam } from './SceneMotion.jsx';
import { MEMBER_KEYS, MEMBERS } from '@config/members/index.js';
import { Avatar } from '../../components/Avatar.jsx';
import { Button } from '../../components/Button.jsx';
import { useDarkChrome } from '../../hooks/useDarkChrome.js';

// 장면 문구가 다 나타나는 시간. 이보다 먼저 누른 탭은 무시합니다(시안 05 S0·T1)
export const SCENE_TAP_READY_MS = 1200;
const ACTS = ['골목', '카운터', '별채'];

export function sceneUrl(name) {
    return `/assets/scenes/${name}.jpg`;
}

export function SceneBackdrop({
    image,
    zoom = true,
    overlay = null,
    position = 'center',
    top = 260,
    topOpacity = 0.7,
    bottom = 440,
    bottomOpacity = 0.88,
    bottomFrom = 40,
    children,
}) {
    // 앱 상태바 · 아래 바 자리도 먹색(DEV-13 0)
    useDarkChrome();
    return (
        <div className="relative flex min-h-dvh flex-col overflow-hidden bg-text-primary text-[#FFFDF8]">
            <img
                src={sceneUrl(image)}
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 size-full object-cover ${zoom ? 'motion-safe:animate-scene-zoom' : ''}`}
                style={{ objectPosition: position }}
            />
            {overlay}
            <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0"
                style={{ height: top, background: `linear-gradient(rgba(38,35,31,${topOpacity}), rgba(38,35,31,0))` }}
            />
            <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0"
                style={{
                    height: bottom,
                    background: `linear-gradient(rgba(38,35,31,0), rgba(38,35,31,${bottomOpacity}) ${bottomFrom}%)`,
                }}
            />
            <div className="relative flex min-h-dvh flex-1 flex-col">{children}</div>
        </div>
    );
}

function actColor(isCurrent, onPhoto) {
    if (onPhoto) {
        return isCurrent ? '#FFFDF8' : '#E3D9C8';
    }
    return isCurrent ? 'var(--color-text-primary)' : 'var(--color-label-muted)';
}

export function ActMarker({ current, onPhoto = true }) {
    return (
        <div aria-label={`지금 장면: ${ACTS[current]}`} className="flex items-center justify-center gap-2 pt-8">
            {ACTS.map((act, index) => (
                <span key={act} className="flex items-center gap-2">
                    {index > 0 ? (
                        <span
                            aria-hidden="true"
                            className="h-px w-5"
                            style={{ background: onPhoto ? 'rgba(255,253,248,0.4)' : 'var(--color-hairline)' }}
                        />
                    ) : null}
                    <span className="text-micro" style={{ color: actColor(index === current, onPhoto) }}>
                        {act}
                    </span>
                </span>
            ))}
        </div>
    );
}

/** 문구가 다 나타날 때까지(1.2초) 기다렸다가 탭을 받습니다 */
function useTapReady() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setReady(true), SCENE_TAP_READY_MS);
        return () => clearTimeout(timer);
    }, []);
    return ready;
}

/** S0·T1 문이 열린다. 화면 어디든 탭 → 카운터(자동으로 넘기지 않음) */
export function CounterTransition({ onNext, fadeIn = true }) {
    const ready = useTapReady();
    const onNextRef = useRef(onNext);
    onNextRef.current = onNext;
    return (
        <button
            type="button"
            onClick={() => {
                if (ready) {
                    onNextRef.current();
                }
            }}
            aria-label="카운터로 들어가기"
            className={`fixed inset-0 z-50 mx-auto block max-w-app text-left ${fadeIn ? 'motion-safe:animate-dim-in' : ''}`}
        >
            <SceneBackdrop image="counter" zoom={false} overlay={<Steam />} top={120} bottom={360} bottomOpacity={0.85}>
                <ActMarker current={1} />
                <p className="m-0 mt-4 text-center text-caption text-[#EEE6D7] italic">(문에 달린 종이 딸랑 울린다)</p>
                {/* 이모 초상은 두지 않습니다. 모바일 폭에서 주전자 김(MO2)을 가립니다(사용자 2026-10-13) */}
                <div className="mt-auto flex flex-col items-center gap-2 px-5 pb-10 text-center">
                    <span className="text-section text-[#E3D9C8]">1막</span>
                    <h2 className="m-0 font-serif text-scene">카운터에서 차 한잔</h2>
                    <p className="m-0 text-[15px] leading-6 text-[#EEE6D7]">카운터 너머로 순례 이모가 고개를 들어요.</p>
                    <span
                        className={`mt-3 text-[12px] leading-[18px] text-[#E3D9C8] transition-opacity duration-500 ${
                            ready ? 'motion-safe:animate-tap-hint' : 'opacity-0'
                        }`}
                    >
                        화면을 누르면 들어가요
                    </span>
                </div>
            </SceneBackdrop>
        </button>
    );
}

/** S0·T2 별채 문이 열린다. 자동으로 넘기지 않고 「별채로 건너가기」를 눌러 들어갑니다 */
export function AnnexTransition({ onEnter, busy = false }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="별채로 건너가기"
            className="fixed inset-0 z-50 mx-auto flex max-w-app flex-col overflow-y-auto bg-bg motion-safe:animate-dim-in"
        >
            <div className="relative h-[540px] shrink-0 overflow-hidden">
                <img
                    src={sceneUrl('annex')}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 size-full object-cover"
                    style={{ objectPosition: 'center 74%' }}
                />
                <LampFlicker objectPositionY={0.74} />
                <span
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-[70px]"
                    style={{ background: 'linear-gradient(rgba(38,35,31,0.6), rgba(38,35,31,0))' }}
                />
                <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-14"
                    style={{ background: 'linear-gradient(rgba(246,241,231,0), var(--color-bg))' }}
                />
                <div className="relative">
                    <ActMarker current={2} />
                </div>
            </div>
            <div className="-mt-6 flex flex-col items-center gap-3 px-5 pb-8 text-center">
                <p className="m-0 text-caption text-text-secondary italic">(이모가 뒷문을 열어 준다)</p>
                <span className="text-section text-label-muted">2막</span>
                <h2 className="m-0 font-serif text-[22px] leading-8 font-bold">별채에 다섯 사람이 둘러앉아 있어요</h2>
                <ul className="m-0 mt-2 grid w-full list-none grid-cols-5 gap-1 p-0">
                    {MEMBER_KEYS.map((memberKey) => (
                        <li key={memberKey} className="flex flex-col items-center gap-1.5">
                            <Avatar memberKey={memberKey} size={56} />
                            <span className="text-[13px] font-semibold">{MEMBERS[memberKey].name}</span>
                        </li>
                    ))}
                </ul>
                <p className="m-0 mt-1 text-micro font-normal text-label-muted">
                    멤버들은 AI가 연기하는 가상 인물이에요.
                </p>
                <Button fullWidth busy={busy} onClick={onEnter} className="mt-2">
                    별채로 건너가기
                </Button>
            </div>
        </div>
    );
}
